import { Receipt } from "@phosphor-icons/react";
import LegalLayout, { Section, P } from "@/components/LegalLayout";

export default function Sales() {
  return (
    <LegalLayout
      eyebrow="Conditions générales de vente"
      icon={Receipt}
      title="Nos conditions de vente."
      intro="Les présentes conditions générales de vente (CGV) régissent la souscription et l'usage d'un abonnement payant au service Questiomed. Elles complètent, sans les remplacer, les conditions générales d'utilisation (CGU) et la politique de confidentialité."
      path="/cgv"
    >
      <Section title="1. Éditeur et coordonnées">
        <p>
          Le service Questiomed est édité par <P /> (ci-après « l'Éditeur »), dont les coordonnées
          complètes figurent dans les <a href="/mentions-legales" className="text-[#3A8FA5] underline underline-offset-2">mentions légales</a>.
        </p>
      </Section>

      <Section title="2. Objet">
        <p>
          Les présentes CGV définissent les conditions dans lesquelles l'Éditeur met à disposition
          des professionnels de santé, en mode Software-as-a-Service (SaaS), un logiciel en ligne
          de conception, d'envoi et d'analyse de questionnaires cliniques (le « Service »).
        </p>
      </Section>

      <Section title="3. Souscripteurs concernés">
        <p>
          Le Service est réservé aux professionnels de santé exerçant en France ou dans l'Union
          européenne, agissant dans le cadre de leur activité professionnelle. La souscription
          suppose l'acceptation sans réserve des présentes CGV, des CGU et de la politique de
          confidentialité.
        </p>
        <p>
          En cochant la case d'acceptation lors de la souscription, le souscripteur reconnaît avoir
          pris connaissance et accepté l'intégralité des présentes conditions.
        </p>
      </Section>

      <Section title="4. Description des formules">
        <p>Deux formules d'abonnement sont proposées :</p>
        <div className="rounded-[14px] border border-[#E1E9F0] bg-white p-5">
          <p className="font-bold text-[#1E3A5F]">Formule Standard</p>
          <p className="text-[#5F758F] mt-1">Envoi illimité de questionnaires, patients illimités, bibliothèque validée, conformité RGPD/HDS.</p>
          <p className="mt-3 text-[#1E3A5F]">
            <strong>29 €/mois TTC</strong> sans engagement — ou <strong>26,10 €/mois TTC</strong> (soit 313,20 €/an) avec engagement annuel (−10 %).
          </p>
        </div>
        <div className="rounded-[14px] border border-[#3A8FA5] ring-2 ring-[#3A8FA5]/20 bg-white p-5">
          <p className="font-bold text-[#1E3A5F]">Formule Premium</p>
          <p className="text-[#5F758F] mt-1">Toutes les fonctionnalités Standard, plus les études cliniques, la comparaison de cohortes, l'analyse IA des documents et les exports CSV.</p>
          <p className="mt-3 text-[#1E3A5F]">
            <strong>49 €/mois TTC</strong> sans engagement — ou <strong>44,10 €/mois TTC</strong> (soit 529,20 €/an) avec engagement annuel (−10 %).
          </p>
        </div>
        <p className="text-[13px] text-[#5F758F]">
          Les prix sont indiqués en euros toutes taxes comprises (TTC), TVA française applicable
          incluse. Un devis peut être établi sur demande pour les établissements de santé.
        </p>
      </Section>

      <Section title="5. Essai gratuit">
        <p>
          Le souscripteur bénéficie d'une <strong>période d'essai gratuit de 15 jours</strong> lui
          donnant accès à l'ensemble des fonctionnalités de la formule Premium, sans avoir à
          renseigner de moyen de paiement. À l'issue de cette période, l'accès aux fonctionnalités
          payantes est suspendu tant qu'aucun abonnement n'est souscrit. Les données créées pendant
          l'essai sont conservées.
        </p>
      </Section>

      <Section title="6. Souscription et paiement">
        <p>
          La souscription s'effectue exclusivement en ligne, via la plateforme de paiement sécurisée
          <strong> Stripe Payments Europe, Ltd.</strong> (Irlande). L'Éditeur ne collecte ni ne
          conserve aucune donnée bancaire. Les modes de paiement acceptés sont : carte bancaire
          (Visa, MasterCard, American Express, Cartes Bancaires), et le cas échéant les méthodes
          locales prises en charge par Stripe.
        </p>
        <p>
          Le paiement est prélevé automatiquement selon la périodicité choisie (mensuelle ou
          annuelle) à la date anniversaire de la souscription.
        </p>
      </Section>

      <Section title="7. Facturation">
        <p>
          Une facture électronique conforme (mentions article 289 du CGI) est émise pour chaque
          échéance et mise à disposition dans l'espace « Paramètres → Abonnement » du compte
          souscripteur. Sur demande, l'Éditeur transmet un duplicata par email.
        </p>
      </Section>

      <Section title="8. Durée, tacite reconduction et résiliation">
        <p>
          <strong>Formule mensuelle sans engagement</strong> : l'abonnement se renouvelle
          tacitement chaque mois. Le souscripteur peut le résilier à tout moment depuis son espace
          personnel ; la résiliation prend effet à l'issue de la période mensuelle en cours et
          aucune somme n'est due au titre de la période suivante.
        </p>
        <p>
          <strong>Formule annuelle avec engagement</strong> : l'abonnement est souscrit pour une
          durée ferme de douze (12) mois. Il se renouvelle ensuite par tacite reconduction pour
          une durée identique, sauf résiliation notifiée au moins un (1) mois avant l'échéance,
          par email à <a href="mailto:contact@questiomed.fr" className="text-[#3A8FA5] underline underline-offset-2">contact@questiomed.fr</a>
          {" "}ou depuis l'espace personnel. En cas de résiliation anticipée avant le terme, les
          mensualités restant à courir jusqu'à la fin de la période d'engagement demeurent dues,
          sauf motif légitime prévu par la loi.
        </p>
      </Section>

      <Section title="9. Droit de rétractation">
        <p>
          Conformément à l'article L. 221-3 du Code de la consommation, le droit de rétractation
          prévu au bénéfice des consommateurs ne s'applique pas aux professionnels agissant dans
          le cadre de leur activité, sauf lorsque leur effectif est inférieur ou égal à cinq
          salariés et que l'objet du contrat n'entre pas dans le champ de leur activité principale.
        </p>
        <p>
          Lorsqu'il est applicable, le droit de rétractation peut être exercé dans un délai de
          quatorze (14) jours à compter de la souscription, par email à
          <a href="mailto:contact@questiomed.fr" className="text-[#3A8FA5] underline underline-offset-2 ml-1">contact@questiomed.fr</a>.
          Le souscripteur reconnaît expressément que, s'il demande l'exécution immédiate du Service
          pendant ce délai, il devra régler au prorata le service utilisé jusqu'à la date de sa
          rétractation.
        </p>
      </Section>

      <Section title="10. Modification des tarifs et des formules">
        <p>
          L'Éditeur se réserve le droit de faire évoluer les tarifs et le périmètre des formules.
          Toute modification est notifiée au souscripteur au moins trente (30) jours avant sa prise
          d'effet, par email et via l'application. Le souscripteur dispose alors du droit de
          résilier son abonnement sans pénalité avant l'entrée en vigueur des nouveaux tarifs.
        </p>
      </Section>

      <Section title="11. Défaut de paiement">
        <p>
          En cas d'échec de prélèvement, l'Éditeur adresse une relance automatique via Stripe.
          À défaut de régularisation dans un délai de sept (7) jours, l'accès au Service peut être
          suspendu jusqu'au paiement effectif. Les sommes dues portent, de plein droit, intérêt au
          taux légal majoré prévu par l'article L. 441-10 du Code de commerce et, s'il s'agit d'un
          professionnel, d'une indemnité forfaitaire de 40 € pour frais de recouvrement.
        </p>
      </Section>

      <Section title="12. Programme de parrainage">
        <p>
          Le souscripteur peut inviter un confrère à créer un compte via un lien de parrainage
          personnel. À l'inscription du filleul, le parrain bénéficie soit d'une prolongation
          gratuite de son essai de trente (30) jours (si son statut est encore en période d'essai),
          soit d'un crédit égal à une mensualité de son abonnement en cours, appliqué à la
          prochaine facture via le solde client Stripe. Le programme ne peut faire l'objet d'aucune
          contrepartie financière et peut être suspendu à tout moment.
        </p>
      </Section>

      <Section title="13. Obligations de l'Éditeur">
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Fournir un Service accessible 24 h / 24 avec un taux de disponibilité cible de 99 %, sauf maintenance planifiée.</li>
          <li>Héberger les données en France chez un tiers certifié HDS et assurer la sécurité technique et organisationnelle.</li>
          <li>Notifier toute violation de données conformément à l'article 33 du RGPD.</li>
          <li>Fournir un support technique par email dans les meilleurs délais.</li>
        </ul>
      </Section>

      <Section title="14. Obligations du souscripteur">
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Utiliser le Service dans le cadre exclusif de son activité professionnelle et dans le respect du secret médical.</li>
          <li>Ne pas partager ses identifiants de connexion et sécuriser son poste de travail.</li>
          <li>Obtenir le consentement libre et éclairé de ses patients avant tout envoi.</li>
          <li>Régler les sommes dues aux échéances convenues.</li>
        </ul>
      </Section>

      <Section title="15. Responsabilité">
        <p>
          L'Éditeur agit en qualité de sous-traitant au sens de l'article 28 du RGPD. Il ne peut
          être tenu responsable de l'interprétation médicale des scores et réponses collectés,
          laquelle relève de la seule appréciation du praticien.
        </p>
        <p>
          La responsabilité totale de l'Éditeur, tous préjudices confondus, est limitée aux sommes
          effectivement versées par le souscripteur au titre des douze (12) mois précédant le fait
          générateur du dommage.
        </p>
      </Section>

      <Section title="16. Suspension et résiliation pour manquement">
        <p>
          En cas de manquement du souscripteur à ses obligations essentielles (défaut de paiement,
          usage frauduleux, violation du secret médical, etc.), l'Éditeur pourra suspendre puis
          résilier l'abonnement quinze (15) jours après une mise en demeure restée sans effet,
          sans préjudice de tous dommages-intérêts.
        </p>
      </Section>

      <Section title="17. Réversibilité et restitution des données">
        <p>
          En cas de résiliation, le souscripteur peut, pendant une période de trente (30) jours,
          exporter ses données (patients, questionnaires, réponses) au format CSV / JSON depuis son
          espace personnel. Passé ce délai, les données sont supprimées de manière irréversible,
          hors obligations légales de conservation.
        </p>
      </Section>

      <Section title="18. Données personnelles et cookies">
        <p>
          Les modalités de traitement des données personnelles sont détaillées dans la
          <a href="/confidentialite" className="text-[#3A8FA5] underline underline-offset-2 ml-1">politique de confidentialité</a>.
          Aucune donnée bancaire n'est stockée par l'Éditeur : toutes les transactions sont opérées
          via Stripe, certifié PCI-DSS niveau 1.
        </p>
      </Section>

      <Section title="19. Médiation de la consommation">
        <p>
          Conformément à l'article L. 616-1 du Code de la consommation, tout souscripteur ayant la
          qualité de consommateur peut recourir gratuitement au médiateur de la consommation
          désigné par l'Éditeur :
        </p>
        <p className="mt-2 rounded-[10px] bg-[#F4F7FB] border border-[#E1E9F0] px-4 py-3">
          <strong>Médiateur de la consommation</strong> : <P /><br />
          Coordonnées : <P /><br />
          Site : <P />
        </p>
        <p>
          Le souscripteur peut également recourir à la plateforme européenne de règlement en ligne
          des litiges : <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer" className="text-[#3A8FA5] underline underline-offset-2">ec.europa.eu/consumers/odr</a>.
        </p>
      </Section>

      <Section title="20. Loi applicable et juridiction">
        <p>
          Les présentes CGV sont soumises au droit français. Tout litige relatif à leur formation,
          leur exécution ou leur interprétation, à défaut de résolution amiable, sera porté devant
          les tribunaux compétents du ressort du siège social de l'Éditeur, sauf disposition d'ordre
          public contraire.
        </p>
      </Section>
    </LegalLayout>
  );
}
